import { createContext, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import analyticsService from "./AnalyticsService";
import {
  AnalyticsErrorBoundary,
  setupGlobalErrorTracking,
} from "./utils/errorTracker";
import { usePageTracker } from "./utils/pageTracker";
import { useInteractionTracker } from "./utils/interactionTracker";
import engagementTracker, {
  ENGAGEMENT_REASONS,
} from "./utils/engagementTracker";
import { resolveDashboardDimension } from "./utils/resolveDashboardDimension";
import { CUSTOM_DIMENSIONS, resolveEnvironment } from "./constants";

const GAContext = createContext(null);

const GAProvider = ({ children }) => {
  const { pathname } = useLocation();
  const authReducer = useSelector((state) => state?.authReducer);
  const orgReducer = useSelector((state) => state?.orgReducer);
  const currentDashboard = useSelector(
    (state) => state?.dashboardReducer?.currentDashboard,
  );
  const environment = resolveEnvironment();
  const userData = authReducer?.userData || {};

  const analyticsUserId =
    userData?.useruid ||
    userData?.clientUserId ||
    userData?.client_User_Id ||
    null;
  const userName =
    [userData?.firstName, userData?.lastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(" ") || null;
  const isLoggedIn = Boolean(authReducer?.isLogin);
  const userRole = userData?.role?.trim() || null;
  const subscriptionPlan = userData?.subscriptionName?.trim() || null;
  const previousUserId = useRef(undefined);

  const analyticsContext = useMemo(() => {
    const selectedFI = orgReducer?.selectedFI || {};
    return {
      [CUSTOM_DIMENSIONS.ENVIRONMENT]: environment || null,
      [CUSTOM_DIMENSIONS.USER_ROLE]: isLoggedIn ? userRole : null,
      [CUSTOM_DIMENSIONS.SUBSCRIPTION_PLAN]: isLoggedIn
        ? subscriptionPlan
        : null,
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION_TYPE]: isLoggedIn
        ? selectedFI?.type || selectedFI?.fi_Type || null
        : null,
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION]: isLoggedIn
        ? selectedFI?.actualName || selectedFI?.org_name || null
        : null,
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION_ID]: isLoggedIn
        ? selectedFI?.orgClientId || selectedFI?.orguid || null
        : null,
      [CUSTOM_DIMENSIONS.IDRSSD]: isLoggedIn
        ? selectedFI?.idrssd || null
        : null,
      [CUSTOM_DIMENSIONS.DASHBOARD]: isLoggedIn
        ? resolveDashboardDimension(currentDashboard, pathname)
        : null,
    };
  }, [
    environment,
    isLoggedIn,
    userRole,
    subscriptionPlan,
    orgReducer,
    currentDashboard,
    pathname,
  ]);

  useEffect(() => {
    analyticsService.init({ disableOnLocal: false });
    // analyticsService.init();
  }, []);

  useEffect(() => {
    analyticsService.setContext(analyticsContext);
  }, [analyticsContext]);

  useEffect(() => {
    const nextUserId = isLoggedIn && analyticsUserId ? analyticsUserId : null;
    // Report the outgoing user's engagement before the GA identity changes.
    if (
      previousUserId.current !== undefined &&
      previousUserId.current !== nextUserId
    ) {
      engagementTracker.flush(ENGAGEMENT_REASONS.USER_CHANGE);
    }
    previousUserId.current = nextUserId;

    if (isLoggedIn && analyticsUserId) {
      analyticsService.setUser(analyticsUserId, {
        [CUSTOM_DIMENSIONS.USER_ID]: analyticsUserId,
        [CUSTOM_DIMENSIONS.USER_NAME]: userName,
        [CUSTOM_DIMENSIONS.USER_ROLE]: userRole,
        [CUSTOM_DIMENSIONS.SUBSCRIPTION_PLAN]: subscriptionPlan,
      });
      return;
    }

    analyticsService.clearUser();
  }, [
    analyticsUserId,
    isLoggedIn,
    orgReducer,
    subscriptionPlan,
    userName,
    userRole,
  ]);

  useEffect(() => {
    return setupGlobalErrorTracking();
  }, []);

  usePageTracker();
  useInteractionTracker();

  return (
    <GAContext.Provider value={analyticsContext}>
      <AnalyticsErrorBoundary>{children}</AnalyticsErrorBoundary>
    </GAContext.Provider>
  );
};

GAProvider.propTypes = {
  children: PropTypes.node,
};

export default GAProvider;
export { GAContext };
